import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Plus,
  Copy,
  Check,
  Lightbulb,
  BookOpen,
  Share2,
  Link
} from 'lucide-react';
import { PILLARS } from '../lib/pillars';
import { calculateProfileScores } from '../lib/calculations';
import {
  runFullAnalysis,
  generateCrossProfileAnalysis,
  generateCombinedSummary
} from '../lib/analysis-engine';
import { HowToReadResults } from './HowToReadResults';
import { ProfileResultCard } from './ProfileResultCard';
import { ExperienceMapComparison } from './ExperienceMapComparison';
import { useShare } from '../lib/useShare';
import jsPDF from 'jspdf';

// ============================================
// COMBINED SUMMARY COMPONENT
// ============================================
const CombinedSummary = ({ profiles }) => {
  const summary = generateCombinedSummary(profiles);
  if (!summary) return null;

  return (
    <Card className="border-border bg-card mt-8" data-testid="combined-summary">
      <CardHeader>
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary" />
          <CardTitle className="text-xl font-light">Your Complete Story</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-foreground leading-relaxed">{summary.narrative}</p>
      </CardContent>
    </Card>
  );
};

// ============================================
// COMPARISON VIEW
// ============================================
const ComparisonView = ({ profiles }) => {
  const crossAnalysis = generateCrossProfileAnalysis(profiles);
  if (!crossAnalysis || crossAnalysis.insights.length === 0) return null;

  return (
    <Card className="border-border bg-card mt-8" data-testid="comparison-view">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-primary" />
          <CardTitle className="text-xl font-light">How These Parts of You Compare</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {crossAnalysis.insights.map((insight, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="text-primary text-sm mt-0.5">&#8226;</span>
            <p className="text-foreground/90 leading-relaxed text-sm">{insight.text}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN RESULTS COMPONENT
// ============================================
export const Results = ({ profiles, onEdit, onRetake, onAddProfile }) => {
  const [activeTab, setActiveTab] = useState(profiles[0]?.id || '');
  const [copiedText, setCopiedText] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const { sharing, shareUrl, createShare } = useShare();

  const handleShare = async () => {
    const url = await createShare('haevn', { profiles });
    if (url) {
      navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    }
  };

  const handleDownload = () => {
    const doc = new jsPDF();
    let yPos = 20;

    doc.setFontSize(18);
    doc.text('HAEVN Profile', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, yPos);
    yPos += 15;

    profiles.forEach((profile) => {
      if (yPos > 240) { doc.addPage(); yPos = 20; }

      const scores = calculateProfileScores(profile);
      const analysis = runFullAnalysis(profile, scores);

      doc.setFontSize(14);
      doc.text(`Context: ${profile.label}`, 20, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.text(`Mode: ${profile.mode === 'current' ? 'Current Reality' : 'Seeking'}`, 20, yPos);
      yPos += 4;
      doc.text(`Intent: ${profile.intent.join(', ')}`, 20, yPos);
      yPos += 4;
      if (profile.mode === 'seeking') {
        const confLabels = { 'yes': 'High', 'somewhat': 'Emerging', 'not-really': 'Exploratory', 'not-sure': 'Unclear' };
        doc.text(`Experience Confidence: ${confLabels[profile.experienceConfidence] || 'N/A'}`, 20, yPos);
      }
      yPos += 8;

      // Intent Anchor
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('INTENT', 20, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 5;
      const anchorLines = doc.splitTextToSize(analysis.intentAnchor, 170);
      doc.text(anchorLines, 20, yPos);
      yPos += anchorLines.length * 5 + 5;

      // What This Means
      if (yPos > 260) { doc.addPage(); yPos = 20; }
      doc.setFont(undefined, 'bold');
      doc.text('WHAT THIS MEANS', 20, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 5;
      const meansLines = doc.splitTextToSize(analysis.whatThisTellsYou, 170);
      doc.text(meansLines, 20, yPos);
      yPos += meansLines.length * 5 + 5;

      // Put It Into Words
      if (analysis.putItIntoWords) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFont(undefined, 'bold');
        doc.text('PUT IT INTO WORDS', 20, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 5;
        const wordLines = doc.splitTextToSize(analysis.putItIntoWords, 170);
        doc.text(wordLines, 20, yPos);
        yPos += wordLines.length * 5 + 5;
      }

      // HAEVN Summary
      if (analysis.haevnSummary) {
        if (yPos > 240) { doc.addPage(); yPos = 20; }
        doc.setFont(undefined, 'bold');
        doc.text('HAEVN PROFILE SUMMARY', 20, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 6;
        const h = analysis.haevnSummary;
        const dl = doc.splitTextToSize(`What drives you: ${h.whatDrivesYou}`, 165);
        doc.text(dl, 25, yPos); yPos += dl.length * 5;
        const nw = doc.splitTextToSize(`What doesn't work: ${h.whatDoesntWork}`, 165);
        doc.text(nw, 25, yPos); yPos += nw.length * 5;
        const lf = doc.splitTextToSize(`What to look for: ${h.whatToLookFor}`, 165);
        doc.text(lf, 25, yPos); yPos += lf.length * 5 + 5;
      }

      yPos += 10;
    });

    // Cross-profile
    if (profiles.length > 1) {
      const cross = generateCrossProfileAnalysis(profiles);
      if (cross?.insights.length > 0) {
        if (yPos > 220) { doc.addPage(); yPos = 20; }
        doc.setFontSize(12);
        doc.text('Cross-Context Insights', 20, yPos);
        yPos += 8;
        doc.setFontSize(10);
        cross.insights.forEach(insight => {
          const lines = doc.splitTextToSize(`- ${insight.text}`, 170);
          doc.text(lines, 20, yPos);
          yPos += lines.length * 5 + 3;
        });
      }
    }

    doc.save('haevn-profile.pdf');
  };

  const handleCopyText = () => {
    let text = 'HAEVN Profile\n';
    text += `Generated: ${new Date().toLocaleDateString()}\n\n`;

    profiles.forEach(profile => {
      const scores = calculateProfileScores(profile);
      const analysis = runFullAnalysis(profile, scores);

      text += `=== ${profile.label} ===\n`;
      text += `Mode: ${profile.mode === 'current' ? 'Current Reality' : 'Seeking'}\n`;
      text += `Intent: ${profile.intent.join(', ')}\n`;
      if (profile.mode === 'seeking') {
        const confLabels = { 'yes': 'High', 'somewhat': 'Emerging', 'not-really': 'Exploratory', 'not-sure': 'Unclear' };
        text += `Experience Confidence: ${confLabels[profile.experienceConfidence] || 'N/A'}\n\n`;
      }

      text += `INTENT\n${analysis.intentAnchor}\n\n`;
      text += `WHAT THIS MEANS\n${analysis.whatThisTellsYou}\n\n`;

      if (analysis.putItIntoWords) {
        text += `PUT IT INTO WORDS\n${analysis.putItIntoWords}\n\n`;
      }

      if (analysis.haevnSummary) {
        const h = analysis.haevnSummary;
        text += 'HAEVN PROFILE SUMMARY\n';
        text += `What drives you: ${h.whatDrivesYou}\n`;
        text += `What doesn't work: ${h.whatDoesntWork}\n`;
        text += `What to look for: ${h.whatToLookFor}\n`;
      }
      text += '\n';
    });

    navigator.clipboard.writeText(text);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  return (
    <section className="min-h-screen py-24 px-6" data-testid="results-section">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
            Your Results
          </span>
          <h2 className="text-3xl sm:text-4xl font-light text-foreground mb-4">
            Your HAEVN Profile
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            What kind of experience you are looking for, what makes it work,
            and why it sometimes does not.
          </p>
        </motion.div>

        {/* How to Read */}
        <HowToReadResults mode={profiles[0]?.mode} />

        {profiles.length > 1 ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="w-full grid" style={{ gridTemplateColumns: `repeat(${Math.min(profiles.length, 4)}, 1fr)` }}>
              {profiles.map(profile => (
                <TabsTrigger
                  key={profile.id}
                  value={profile.id}
                  className="text-xs truncate"
                  data-testid={`tab-${profile.id}`}
                >
                  {profile.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {profiles.map(profile => (
              <TabsContent key={profile.id} value={profile.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <ProfileResultCard profile={profile} />
                </motion.div>
              </TabsContent>
            ))}

            {/* Cross-profile sections */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-8"
            >
              <ExperienceMapComparison profiles={profiles} />
            </motion.div>
            <ComparisonView profiles={profiles} />
            <CombinedSummary profiles={profiles} />
          </Tabs>
        ) : profiles.length === 1 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ProfileResultCard profile={profiles[0]} />
          </motion.div>
        ) : null}

        {/* Final Anchor Line */}
        <motion.p
          className="mt-16 text-center text-sm text-muted-foreground italic max-w-xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          data-testid="final-anchor-line"
        >
          This is not about having more sex. It is about having the kind of experience that actually works for you.
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Button
            onClick={onEdit}
            variant="outline"
            className="rounded-full px-6"
            data-testid="edit-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Edit Answers
          </Button>

          <Button
            onClick={onRetake}
            variant="outline"
            className="rounded-full px-6"
            data-testid="retake-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Start Over
          </Button>

          <Button
            onClick={onAddProfile}
            variant="outline"
            className="rounded-full px-6"
            data-testid="add-profile-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Context
          </Button>

          <Button
            onClick={handleDownload}
            className="rounded-full px-6"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>

          <Button
            onClick={handleCopyText}
            variant="ghost"
            className="rounded-full px-6"
            data-testid="copy-text-btn"
          >
            {copiedText ? (
              <><Check className="w-4 h-4 mr-2" />Copied</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" />Copy as Text</>
            )}
          </Button>

          <Button
            onClick={handleShare}
            variant="outline"
            className="rounded-full px-6"
            disabled={sharing}
            data-testid="share-btn"
          >
            {copiedLink ? (
              <><Check className="w-4 h-4 mr-2" />Link Copied</>
            ) : sharing ? (
              <>Creating link...</>
            ) : (
              <><Share2 className="w-4 h-4 mr-2" />Share Results</>
            )}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
